import { createSlice } from "@reduxjs/toolkit";
import { COUNTRY_INFO } from "../../constans/countries/countries";
import { auth } from "../../firebase.config";
import { addCountryToWishlist, 
         loadWishlistFromFirestore, 
         removeCountryFromWishlist, 
         saveWishlistToFirestore, 
         updatePriorityInFirestore} from "../../constans/functions/wishlistServices";
import { loadWishlistFromStorage, saveWishlistToStorage } from "../../constans/functions/storage";
import { showWishlistNotification } from "../../constans/functions/notificationSlice";

export const PRIORITIES = {
    MUST: 'must',
    NEXT: 'next',
    LATER: 'later'
};

const updateCountryInfoWish = (wishlist, priorities = {}) => {
  Object.keys(COUNTRY_INFO).forEach(e => {
    COUNTRY_INFO[e].wish = false;
    COUNTRY_INFO[e].wish_priority = null;
  });
  
  wishlist.forEach(e => {
    if (COUNTRY_INFO[e]) {
      COUNTRY_INFO[e].wish = true;
      COUNTRY_INFO[e].wish_priority = priorities[e] || null;
    }
  });
};

const normalizeFirestoreData = (data) => {
    if (!data || data.length === 0) {
        return { wishlist: [], priorities: {} };
    }
        
    if (typeof data[0] === 'number') {
        return { wishlist: data, priorities: {} };
    }
    
    if (typeof data[0] === 'object' && data[0].countryId !== undefined) {
        const wishlist = data.map(e => e.countryId);
        const priorities = {};
        data.forEach(e => {
            if (e.priority) {
                priorities[e.countryId] = e.priority;
            }
        });
        return { wishlist, priorities };
    }
    
    return { wishlist: data, priorities: {} };
};

export const wishSlice = createSlice({
    name: 'wishList',
    initialState: {
        wishlist: [],
        priorities: {},
        isLoading: false,
        isLoadedFromCache: false
    },
    reducers: {
        setWishlist: (state, { payload }) => {
            const { wishlist, priorities } = normalizeFirestoreData(payload);
            state.wishlist = wishlist;
            state.priorities = priorities;
            Object.keys(state.priorities).forEach(e => {
                if (!state.wishlist.includes(Number(e))) {
                    delete state.priorities[e];
                }
            });
            
            updateCountryInfoWish(state.wishlist, state.priorities);
            saveWishlistToStorage({ 
                wishlist: state.wishlist, 
                priorities: state.priorities 
            });
        },

        setWishlistFromCache: (state, { payload }) => {
            if (Array.isArray(payload)) {
                state.wishlist = payload;
                state.priorities = {};
            } else {
                state.wishlist = payload.wishlist || [];
                state.priorities = payload.priorities || {};
            }
            updateCountryInfoWish(state.wishlist, state.priorities);
            state.isLoadedFromCache = true;
        },

        addToWishList: (state, { payload }) => {
            if (!state.wishlist.includes(payload)) {
                state.wishlist.push(payload);
                state.priorities[payload] = null;
                if (COUNTRY_INFO[payload]) {
                    COUNTRY_INFO[payload].wish = true;
                    COUNTRY_INFO[payload].wish_priority = null;
                }
                saveWishlistToStorage({ 
                    wishlist: state.wishlist, 
                    priorities: state.priorities 
                });
            }
        },

        removeFromWishList: (state, { payload }) => {
            state.wishlist = state.wishlist.filter(e => e !== payload);
            delete state.priorities[payload];
            if (COUNTRY_INFO[payload]) {
                COUNTRY_INFO[payload].wish = false;
                COUNTRY_INFO[payload].wish_priority = null;
            }
            saveWishlistToStorage({ 
                wishlist: state.wishlist, 
                priorities: state.priorities 
            });
        },
        
        updatePriority: (state, { payload }) => {
            const { countryCode, priority } = payload;
            if (state.wishlist.includes(countryCode)) {
                state.priorities[countryCode] = priority;
                if (COUNTRY_INFO[countryCode]) {
                    COUNTRY_INFO[countryCode].wish_priority = priority;
                }
                saveWishlistToStorage({ 
                    wishlist: state.wishlist, 
                    priorities: state.priorities 
                });
            }
        },

        clearWishList: (state) => {
            state.wishlist.forEach(code => {
                if (COUNTRY_INFO[code]) {
                    COUNTRY_INFO[code].wish = false;
                    COUNTRY_INFO[code].wish_priority = null;
                }
            });
            state.wishlist = [];
            state.priorities = {};
            saveWishlistToStorage({ 
                wishlist: [], 
                priorities: {} 
            });
        },

        setLoading: (state, { payload }) => {
            state.isLoading = payload;
        },

        setLoadedFromCache: (state, { payload }) => {
            state.isLoadedFromCache = payload;
        },
    }
});

export const { 
    addToWishList, 
    removeFromWishList,
    updatePriority,
    clearWishList,
    setWishlist,
    setWishlistFromCache,
    setLoading
} = wishSlice.actions;

// Thunks
export const addCountryToWishlistSync = (countryCode) => async (dispatch, getState) => {
    try {
        dispatch(addToWishList(countryCode));
        if (auth.currentUser) {
            const { wishlist, priorities } = getState().wishList;
            const wishlistWithPriorities = wishlist.map(id => ({
                countryId: id,
                priority: priorities[id] || null
            }));
            await saveWishlistToFirestore(auth.currentUser.uid, wishlistWithPriorities);
        }
        dispatch(showWishlistNotification());
    } catch (err) {
        console.error('Error adding country to wishlist:', err);
        dispatch(removeFromWishList(countryCode));
    }
};

export const removeCountryFromWishlistSync = (countryCode) => async (dispatch, getState) => {
    try {
        dispatch(removeFromWishList(countryCode));
        if (auth.currentUser) {
            const { wishlist, priorities } = getState().wishList;
            const wishlistWithPriorities = wishlist.map(id => ({
                countryId: id,
                priority: priorities[id] || null
            }));
            await saveWishlistToFirestore(auth.currentUser.uid, wishlistWithPriorities);
        }
    } catch (err) {
        console.error('Error removing country from wishlist:', err);
        dispatch(addToWishList(countryCode));
    }
};

export const updatePrioritySync = (countryCode, priority) => async (dispatch) => {
    try {
        dispatch(updatePriority({ countryCode, priority }));
        if (auth.currentUser) {
            await updatePriorityInFirestore(auth.currentUser.uid, countryCode, priority);
        }
    } catch (err) {
        console.error('Error updating priority:', err);
    }
};

export const clearWishlistWithSync = () => async (dispatch) => {
    try {
        dispatch(clearWishList());
        if (auth.currentUser) {
            await saveWishlistToFirestore(auth.currentUser.uid, []);
        }
    } catch (error) {
        console.error('Error clearing wishlist:', error);
    }
};

export const loadUserWishlist = () => async (dispatch) => {

    if (!auth.currentUser) return;

    const cachedData = loadWishlistFromStorage();
    let cachedWishlist = [];
    let cachedPriorities = {};
    
    if (cachedData) {
        if (Array.isArray(cachedData)) {
            cachedWishlist = cachedData;
        } else {
            cachedWishlist = cachedData.wishlist || [];
            cachedPriorities = cachedData.priorities || {};
        }
    }
    
    if (cachedWishlist.length > 0) {
        dispatch(setWishlistFromCache({ wishlist: cachedWishlist, priorities: cachedPriorities }));
    }

    try {
        dispatch(setLoading(true));
        const firestoreData = await loadWishlistFromFirestore(auth.currentUser.uid);
        
        const { wishlist: newWishlist, priorities: newPriorities } = normalizeFirestoreData(firestoreData);
        
        if (JSON.stringify(cachedWishlist) !== JSON.stringify(newWishlist)) {
            const wishlistWithPriorities = newWishlist.map(id => ({
                countryId: id,
                priority: newPriorities[id] || null
            }));
            dispatch(setWishlist(wishlistWithPriorities));
        } else if (JSON.stringify(cachedPriorities) !== JSON.stringify(newPriorities)) {
            Object.keys(newPriorities).forEach(id => {
                dispatch(updatePriority({ countryCode: Number(id), priority: newPriorities[id] }));
            });
        }
        dispatch(setLoading(false));
        
    } catch (err) {
        console.error('Error loading wishlist from Firestore:', err);
        dispatch(setLoading(false));
    }
};